/**
 * Projectile VFX Config — canonical fallback sizing for rendered projectiles.
 *
 * THE PARITY PROBLEM this solves:
 *   A projectile's visual size (width/height) can be set per-ability via
 *   `withProjectile({ width, height })`. When an ability omits them, three
 *   render paths each fell back to their OWN hardcoded defaults:
 *     - ProjectileBehavior (local single)   → 35 × 3
 *     - MultiProjectileBehavior (local multi)→ 15 × 15
 *     - EntityEventWiring (REMOTE single)    → 20 × 4
 *   So a locally-cast projectile and the SAME projectile spawned from another
 *   player's broadcast rendered at different sizes. This file is the single
 *   source of truth all three paths read, so local and remote match exactly.
 *
 * Two-section file (per the repo `*.config.ts` pattern, DJ 2026-04-25):
 *
 *   1. PROJECTILE_VFX_DEFAULTS — immutable production defaults.
 *   2. PROJECTILE_VFX_DEV_OVERRIDE — partial override applied on top while
 *      iterating; leave `{}` to ship production defaults.
 *
 * Consumers import `PROJECTILE_VFX` (the merged result) — never the raw
 * defaults or override slice — so the merge happens in one canonical place.
 */

export interface ProjectileSize {
  /** Projectile length along its travel axis, in world pixels. */
  width: number;
  /** Projectile thickness perpendicular to travel, in world pixels. */
  height: number;
}

/**
 * Per-shape NATURAL ASPECT — the canonical thickness-to-length ratio
 * (`height / width`) of each projectile SHAPE.
 *
 * THE ASYMMETRY PROBLEM this solves (Phase 0b):
 *   `vfx.projectileSize` is a single SCALAR. A scalar can drive symmetric
 *   shapes (round/orb/sphere → square) but cannot encode an aspect ratio, so
 *   asymmetric shapes (arrow 35×3, star 35×15) used to hardcode an explicit
 *   width/height pair in every ability file AND each render path applied the
 *   scalar to BOTH dims (rendering an arrow as a fat square). This map lets a
 *   single scalar reproduce the shape's natural aspect: a scalar is treated as
 *   the travel-axis length (`width`); `height = width * SHAPE_ASPECT[shape]`.
 *
 * Ratios are derived from the existing canonical hardcodes so NOTHING changes
 * visually — this is a refactor to one source, not a retune:
 *   - arrow  35×3  → 3/35   (RangerAuto + SHAPE_DEFAULTS.ARROW)
 *   - star   35×15 → 15/35  (AssassinAuto)
 *   - rect/bolt 20×4 / 15×3 → 4/20 = 1/5  (SHAPE_DEFAULTS.RECT + Nova default)
 *   - round (circle/orb/sphere) → 1 (square: height == width)
 *
 * Shapes NOT listed here fall back to a square aspect (1:1) — that is the
 * correct natural aspect for all round shapes (circle/orb/sphere) and any new
 * symmetric shape. An explicit per-ability width/height ALWAYS wins over this
 * derivation.
 */
export const SHAPE_ASPECT: Record<string, number> = {
  arrow: 3 / 35,
  star: 15 / 35,
  rect: 1 / 5,
  bolt: 1 / 5,
};

/** Fallback aspect for any shape not in `SHAPE_ASPECT` — square (1:1). */
export const DEFAULT_SHAPE_ASPECT = 1;

/**
 * Resolve the natural `height / width` ratio for a projectile shape, falling
 * back to a square aspect (1:1) for round/unlisted shapes. Single helper so
 * every consumer (AbilityBuilder, NovaProjectileBehavior, TomlAbilityFactory)
 * derives the same height from a scalar.
 */
export function shapeAspect(shape: string | undefined): number {
  if (shape && shape in SHAPE_ASPECT) return SHAPE_ASPECT[shape];
  return DEFAULT_SHAPE_ASPECT;
}

/**
 * Derive a width/height pair from a single scalar size scaled by the shape's
 * natural aspect. The scalar is the travel-axis length (`width`);
 * `height = round(width * aspect)`. Reproduces the canonical asymmetric sizes
 * exactly when fed their reference length:
 *   arrow @35 → 35×3, star @35 → 35×15, round @N → N×N.
 */
export function deriveProjectileSize(scalar: number, shape: string | undefined): ProjectileSize {
  return { width: scalar, height: Math.round(scalar * shapeAspect(shape)) };
}

export interface ProjectileVfxConfig {
  /**
   * Fallback size for a single projectile (basic shot). Used by BOTH the
   * local cast path (ProjectileBehavior) and the remote broadcast path
   * (EntityEventWiring) — these MUST match so the same projectile looks
   * identical on the caster's and the observer's screens.
   */
  single: ProjectileSize;
  /**
   * Fallback size for multi-projectile casts (star-burst style). Distinct
   * from `single` because the default shape differs (square-ish star vs
   * thin bolt).
   */
  multi: ProjectileSize;
}

export const PROJECTILE_VFX_DEFAULTS: ProjectileVfxConfig = {
  // Canonical single-projectile fallback. Local + remote read this exact
  // value, eliminating the old 35×3 (local) vs 20×4 (remote) split.
  single: { width: 35, height: 3 },
  // Multi-projectile fallback (was 15×15 in MultiProjectileBehavior).
  multi: { width: 15, height: 15 },
};

/**
 * Per-dev-environment overrides. Partial — anything omitted falls through to
 * `PROJECTILE_VFX_DEFAULTS`. Add temporary tuning here while iterating.
 *
 * Examples:
 *   single: { width: 60, height: 8 }   // fatter bolt for visibility QA
 */
export const PROJECTILE_VFX_DEV_OVERRIDE: Partial<ProjectileVfxConfig> = {};

export const PROJECTILE_VFX: ProjectileVfxConfig = {
  ...PROJECTILE_VFX_DEFAULTS,
  ...PROJECTILE_VFX_DEV_OVERRIDE,
};
